class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.drawer = this;
    this.overlay = this.querySelector('.cart-drawer__overlay');
    this.closeButton = this.querySelector('.cart-drawer__close-button');
    this.checkoutButton = this.querySelector('.cart-drawer__checkout-button');
    this.deliveryOptions = this.querySelectorAll('.cart-drawer__delivery-option');
    this.postalInput = this.querySelector('#cart-drawer-postal');
    this.loader = this.querySelector('#cart-drawer-loader');
    
    this.closeButton?.addEventListener('click', this.close.bind(this));
    this.overlay?.addEventListener('click', this.close.bind(this));
    this.deliveryOptions?.forEach(option => {
      option.addEventListener('click', this.handleDeliveryOption.bind(this));
    });
    
    this.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') this.close();
    });

    // Handle quantity changes - use event delegation
    this.addEventListener('click', this.handleCartDrawerClick.bind(this));
    
    // Set header height for positioning
    this.updateHeaderHeight();
    window.addEventListener('resize', () => this.updateHeaderHeight());
    
    // Load recommendations
    this.loadRecommendations();
  }

  showLoader() {
    if (this.loader) {
      this.loader.classList.add('active');
    }
  }

  hideLoader() {
    if (this.loader) {
      this.loader.classList.remove('active');
    }
  }

  updateHeaderHeight() {
    const header = document.querySelector('#shopify-section-header');
    if (header) {
      const headerHeight = header.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    }
  }

  open() {
    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
    trapFocus(this);
    this.querySelector('.cart-drawer__inner')?.focus();
    
    // Add click outside handler
    this.handleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.handleClickOutside);
  }

  close() {
    this.removeAttribute('open');
    document.body.style.overflow = '';
    removeTrapFocus(this.activeElement);
    
    // Remove click outside handler
    if (this.handleClickOutside) {
      document.removeEventListener('click', this.handleClickOutside);
    }
  }

  handleClickOutside(event) {
    // Check if click is outside the drawer inner area
    const drawerInner = this.querySelector('.cart-drawer__inner');
    if (drawerInner && !drawerInner.contains(event.target)) {
      // Don't close if clicking on cart icon
      const cartIcon = document.getElementById('cart-icon-bubble');
      if (cartIcon && cartIcon.contains(event.target)) {
        return;
      }
      // Don't close if clicking on header
      const header = document.querySelector('#shopify-section-header');
      if (header && header.contains(event.target)) {
        return;
      }
      this.close();
    }
  }

  handleDeliveryOption(event) {
    const clickedOption = event.currentTarget;
    const deliveryType = clickedOption.dataset.deliveryType;
    
    this.deliveryOptions.forEach(option => {
      option.classList.remove('cart-drawer__delivery-option--active');
    });
    
    clickedOption.classList.add('cart-drawer__delivery-option--active');
    
    // Store selected delivery type
    this.selectedDeliveryType = deliveryType;
  }

  handleCartDrawerClick(event) {
    // Handle close button
    const closeButton = event.target.closest('.cart-drawer__close-button');
    if (closeButton) {
      event.preventDefault();
      event.stopPropagation();
      this.close();
      return;
    }
    
    // Handle remove button
    const removeButton = event.target.closest('.cart-drawer__item-remove');
    if (removeButton) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      const index = removeButton.dataset.index;
      this.updateQuantity(index, 0);
      return;
    }
    
    // Handle quantity buttons - using cart-drawer specific classes
    const quantityButton = event.target.closest('.cart-drawer-quantity__button');
    if (quantityButton && quantityButton.closest('cart-drawer')) {
      event.preventDefault();
      event.stopPropagation();
      
      // Prevent multiple clicks
      if (quantityButton.hasAttribute('data-updating')) {
        return;
      }
      quantityButton.setAttribute('data-updating', 'true');
      
      const index = quantityButton.dataset.index;
      const input = this.querySelector(`.cart-drawer-quantity__input[data-index="${index}"]`);
      
      if (!input) {
        quantityButton.removeAttribute('data-updating');
        return;
      }
      
      const currentQuantity = parseInt(input.value) || 0;
      let newQuantity;
      
      if (quantityButton.name === 'plus') {
        newQuantity = currentQuantity + 1;
      } else if (quantityButton.name === 'minus') {
        newQuantity = Math.max(0, currentQuantity - 1);
      } else {
        quantityButton.removeAttribute('data-updating');
        return;
      }
      
      // Update input value visually
      input.value = newQuantity;
      
      // Update quantity in cart
      this.updateQuantity(index, newQuantity).finally(() => {
        quantityButton.removeAttribute('data-updating');
      });
      
      return;
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer__content'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-drawer-count',
        section: 'cart-drawer',
        selector: '#cart-drawer-count'
      }
    ];
  }

  async updateQuantity(line, quantity) {
    // Prevent concurrent updates for the same line item
    const updateKey = `line-${line}`;
    
    if (!this.pendingUpdates) {
      this.pendingUpdates = new Set();
    }
    
    if (this.pendingUpdates.has(updateKey)) {
      return Promise.resolve();
    }
    
    this.pendingUpdates.add(updateKey);
    this.showLoader();
    
    const body = JSON.stringify({
      line,
      quantity,
      sections: ['cart-icon-bubble'],
      sections_url: window.location.pathname
    });

    try {
      const response = await fetch(`${routes.cart_change_url}`, {...fetchConfig(), ...{ body }});
      
      if (!response.ok) {
        throw new Error('Failed to update cart');
      }
      
      const state = await response.text();
      const parsedState = JSON.parse(state);
      
      // Update cart icon
      if (parsedState.sections && parsedState.sections['cart-icon-bubble']) {
        const cartIconSection = document.getElementById('cart-icon-bubble');
        if (cartIconSection) {
          const html = new DOMParser()
            .parseFromString(parsedState.sections['cart-icon-bubble'], 'text/html');
          const newContent = html.querySelector('.shopify-section');
          if (newContent) {
            cartIconSection.innerHTML = newContent.innerHTML;
          }
        }
      }

      // Reload drawer content from server
      await this.reloadDrawerFromServer();
      
    } catch (error) {
      console.error('Error updating cart:', error);
      // Revert input value on error
      const input = this.querySelector(`.cart-drawer-quantity__input[data-index="${line}"]`);
      if (input) {
        try {
          const cartResponse = await fetch('/cart.js');
          const cartData = await cartResponse.json();
          const item = cartData.items.find(item => item.index === parseInt(line) - 1);
          if (item) {
            input.value = item.quantity;
          }
        } catch (e) {
          console.error('Error reverting quantity:', e);
        }
      }
    } finally {
      this.hideLoader();
      this.pendingUpdates.delete(updateKey);
    }
  }

  async reloadDrawerFromServer() {
    // Don't show loader if it's already showing (e.g., from updateQuantity)
    const loaderWasActive = this.loader?.classList.contains('active');
    if (!loaderWasActive) {
      this.showLoader();
    }
    
    try {
      // Fetch cart data with cache-busting to ensure fresh data
      const cacheBuster = Date.now();
      const response = await fetch(`/cart.js?t=${cacheBuster}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache'
        }
      });
      const cartData = await response.json();
      
      // Update cart icon bubble in header
      await this.updateCartIconBubble(cartData.item_count);
      
      // Update count in drawer
      const countElement = this.querySelector('#cart-drawer-count');
      if (countElement) {
        countElement.textContent = cartData.item_count;
      }

      const content = this.querySelector('.cart-drawer__content');
      
      if (cartData.item_count === 0) {
        // Show empty state with collections
        if (content) {
          let collectionsHTML = '';
          const collections = window.cartDrawerCollections || [];
          
          if (collections.length > 0) {
            collectionsHTML = '<div class="cart-drawer__empty-collections"><div class="cart-drawer__empty-collections-grid">';
            
            collections.forEach(collection => {
              if (collection && collection.image && collection.title && collection.url) {
                collectionsHTML += `
                  <div class="cart-drawer__empty-collection">
                    <a href="${collection.url}" class="cart-drawer__empty-collection-link">
                      <div class="cart-drawer__empty-collection-image">
                        <img src="${collection.image}" alt="${collection.title}" loading="lazy" width="300" height="300">
                      </div>
                      <div class="cart-drawer__empty-collection-title">${collection.title.toUpperCase()}</div>
                    </a>
                  </div>
                `;
              }
            });
            
            collectionsHTML += '</div></div>';
          }
          
          content.innerHTML = `
            <div class="cart-drawer__empty">
            <button type="button" class="cart-drawer__close-button" aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" fill="none" viewBox="0 0 18 17">
              <path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor">
              </path>
            </svg>
          </button>
              <h3 class="cart-drawer__empty-title">Your Cart is Empty</h3>
              <p class="cart-drawer__empty-text">Stock up on clean, high-quality protein — responsibly raised and flash-frozen for peak freshness.</p>
              ${collectionsHTML}
              <a href="/collections/all" class="button cart-drawer__empty-button">
                Shop all PRODUCTS
              </a>
              <span class="cart-drawer__empty-divider">Free Delivery on Orders Over $199</span>
            </div>
          `;
        }
        return;
      }

      // If content shows empty state, we need to restore the full structure
      if (content && content.querySelector('.cart-drawer__empty')) {
        // Restore the structure from the original template
        content.innerHTML = `
          
          <div class="cart-drawer__summary">
          <button type="button" class="cart-drawer__close-button" aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" fill="none" viewBox="0 0 18 17">
              <path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor">
              </path>
            </svg>
          </button>
            <div class="cart-drawer__total">
              <h2 class="cart-drawer__title">My Cart (<span id="cart-drawer-count">${cartData.item_count}</span>)</h2>
              <span class="cart-drawer__total-price"></span>
            </div>
          </div>
          <div id="storePickupApp"></div>
          <form action="/cart" method="post" class="cart-drawer__footer-form">
            <button type="submit" name="checkout" class="cart-drawer__footer-checkout-button">
              PROCEED TO CHECKOUT
            </button>
          </form>
          <div class="cart-drawer__items-header">
            <span class="cart-drawer__items-header-label">Product</span>
            <span class="cart-drawer__items-header-label">Total</span>
          </div>
          <div class="cart-drawer__items" id="cart-drawer-items"></div>
          <div class="cart-drawer__recommendations">
            <div class="cart-drawer__recommendations-header">
              <span class="cart-drawer__recommendations-title">Pairs Nicely With</span>
            </div>
            <div class="cart-drawer__recommendations-list" id="cart-drawer-recommendations"></div>
          </div>
          <div class="cart-drawer__footer-summary" id="cart-drawer-footer-summary">
            <div class="cart-drawer__footer-title">Order Summary</div>
            <div class="cart-drawer__footer-row">
              <span class="cart-drawer__footer-label">Subtotal</span>
              <span class="cart-drawer__footer-subtotal"></span>
            </div>
            <div class="cart-drawer__footer-row" id="cart-drawer-footer-savings-row" style="display: none;">
              <span class="cart-drawer__footer-label">My Savings</span>
              <span class="cart-drawer__footer-savings"></span>
            </div>
            <div class="cart-drawer__footer-divider"></div>
            <div class="cart-drawer__footer-row cart-drawer__footer-row--total">
              <span class="cart-drawer__footer-label">Total</span>
              <span class="cart-drawer__footer-total"></span>
            </div>
          </div>
        `;
        
        // Initialize storePickupApp after restoring structure
        // Element is already created, need to trigger initialization
        setTimeout(() => {
          const storePickupApp = this.querySelector('#storePickupApp');
          if (storePickupApp) {
            // Clear the element first to ensure fresh initialization
            storePickupApp.innerHTML = '';
            // Dispatch event to initialize the app
            document.dispatchEvent(new CustomEvent("zapiet:start"));
          }
        }, 200);
      }

      // Update totals first to ensure savings element is created
      await this.updateTotals(cartData);
      
      // Update items (this shouldn't affect summary, but just in case)
      await this.updateCartItems(cartData);
      
      // Re-update totals after items update to ensure savings persists
      await this.updateTotals(cartData);
      
      // Update footer summary
      await this.updateFooterSummary(cartData);
      
      // Ensure storePickupApp is initialized after all updates
      setTimeout(() => {
        const storePickupApp = this.querySelector('#storePickupApp');
        if (storePickupApp && !storePickupApp.hasChildNodes()) {
          // If element is empty, trigger initialization
          document.dispatchEvent(new CustomEvent("zapiet:start"));
        }
      }, 300);
      
      // Reload recommendations
      setTimeout(() => {
        this.loadRecommendations();
      }, 100);
      
    } catch (error) {
      console.error('Error reloading drawer:', error);
    } finally {
      if (!loaderWasActive) {
        this.hideLoader();
      }
    }
  }

  async updateCartIconBubble(itemCount) {
    try {
      // Fetch cart-icon-bubble section
      const response = await fetch(`${window.location.pathname}?sections=cart-icon-bubble`);
      const html = await response.text();
      const parsed = JSON.parse(html);
      
      if (parsed['cart-icon-bubble']) {
        const cartIconElement = document.getElementById('cart-icon-bubble');
        if (cartIconElement) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(parsed['cart-icon-bubble'], 'text/html');
          const newContent = doc.querySelector('.shopify-section');
          if (newContent) {
            cartIconElement.innerHTML = newContent.innerHTML;
          } else {
            // Fallback: update manually
            const cartCountBubble = cartIconElement.querySelector('.cart-count-bubble');
            if (itemCount > 0) {
              if (!cartCountBubble) {
                const bubble = document.createElement('div');
                bubble.className = 'cart-count-bubble';
                const span = document.createElement('span');
                span.setAttribute('aria-hidden', 'true');
                span.textContent = itemCount < 100 ? itemCount : '';
                bubble.appendChild(span);
                cartIconElement.appendChild(bubble);
              } else {
                const span = cartCountBubble.querySelector('span[aria-hidden="true"]');
                if (span) {
                  span.textContent = itemCount < 100 ? itemCount : '';
                }
              }
            } else if (cartCountBubble) {
              cartCountBubble.remove();
            }
          }
        }
      }
    } catch (error) {
      console.error('Error updating cart icon bubble:', error);
      // Fallback: manual update
      const cartIconElement = document.getElementById('cart-icon-bubble');
      if (cartIconElement) {
        const cartCountBubble = cartIconElement.querySelector('.cart-count-bubble');
        if (itemCount > 0) {
          if (!cartCountBubble) {
            const bubble = document.createElement('div');
            bubble.className = 'cart-count-bubble';
            const span = document.createElement('span');
            span.setAttribute('aria-hidden', 'true');
            span.textContent = itemCount < 100 ? itemCount : '';
            bubble.appendChild(span);
            cartIconElement.appendChild(bubble);
          } else {
            const span = cartCountBubble.querySelector('span[aria-hidden="true"]');
            if (span) {
              span.textContent = itemCount < 100 ? itemCount : '';
            }
          }
        } else if (cartCountBubble) {
          cartCountBubble.remove();
        }
      }
    }
  }

  async updateCartItems(cartData) {
    const itemsContainer = this.querySelector('#cart-drawer-items');
    if (!itemsContainer) return;

    // First, save existing subtitles from current HTML before updating
    const existingItems = itemsContainer.querySelectorAll('.cart-drawer__item');
    const subtitleCache = new Map();
    existingItems.forEach(item => {
      const productId = item.dataset.productId;
      const subtitleElement = item.querySelector('.cart-drawer__item-subtitle');
      if (productId && subtitleElement) {
        subtitleCache.set(String(productId), subtitleElement.textContent.trim());
      }
    });

    // Also get subtitles from recommended products data (for newly added items)
    const recommendedProducts = window.cartDrawerRecommendedProducts || [];
    const recommendedSubtitles = new Map();
    recommendedProducts.forEach(product => {
      if (product && product.id && product.subtitle) {
        recommendedSubtitles.set(String(product.id), product.subtitle);
      }
    });

    // Fetch product data for each item to get compare_at_price and metafields
    const itemsWithComparePrice = await Promise.all(
      cartData.items.map(async (item) => {
        let compareAtPrice = null;
        let subtitle = null;
        
        // First, try to get subtitle from cache (existing HTML)
        const cachedSubtitle = subtitleCache.get(String(item.product_id));
        if (cachedSubtitle) {
          subtitle = cachedSubtitle;
        } else {
          // If not in cache, try to get from recommended products data
          const recommendedSubtitle = recommendedSubtitles.get(String(item.product_id));
          if (recommendedSubtitle) {
            subtitle = recommendedSubtitle;
          }
        }
        
        try {
          // Try to get product handle from URL or fetch product data
          const productUrl = item.url || `/products/${item.handle || item.product_id}`;
          const productHandle = productUrl.match(/\/products\/([^\/\?]+)/)?.[1];
          
          if (productHandle) {
            const productResponse = await fetch(`/products/${productHandle}.js`);
            if (productResponse.ok) {
              const productData = await productResponse.json();
              // Find the variant that matches this cart item
              const variant = productData.variants?.find(v => v.id === item.variant_id);
              if (variant && variant.compare_at_price) {
                compareAtPrice = variant.compare_at_price;
              }
              // Try to get subtitle metafield from API (if not already cached)
              if (!subtitle) {
                // Note: /products/{handle}.js doesn't include metafields by default
                // We'll need to use GraphQL or storefront API, but for now use cache
                // Or try to parse from product data if available
                if (productData.metafields && productData.metafields.descriptors && productData.metafields.descriptors.subtitle) {
                  subtitle = productData.metafields.descriptors.subtitle;
                } else if (productData.metafields && productData.metafields.descriptors && productData.metafields.descriptors.subtitle?.value) {
                  subtitle = productData.metafields.descriptors.subtitle.value;
                }
              }
            }
          }
        } catch (e) {
          // Ignore errors, will use fallback
          console.warn('Could not fetch product data for item:', item.title, e);
        }
        
        return {
          ...item,
          compare_at_price: compareAtPrice,
          subtitle: subtitle
        };
      })
    );
    
    // Build items HTML with correct price display logic (matching Liquid template)
    let itemsHTML = '';
    
    itemsWithComparePrice.forEach((item, index) => {
      const originalPrice = item.original_price;
      const finalPrice = item.final_price;
      const compareAtPrice = item.compare_at_price;
      
      // Match Liquid logic:
      // If original_price != final_price: show original_price (strikethrough), final_price, and badge if compare_at_price exists
      // If original_price == final_price: show final_price, and if compare_at_price exists, show compare_at_price (strikethrough) and badge
      
      let priceHTML = '';
      
      if (originalPrice !== finalPrice) {
        // Line-level discount
        priceHTML = `
          <s class="cart-drawer__item-old-price">${this.formatMoney(originalPrice)}</s>
          <span class="cart-drawer__item-final-price">${this.formatMoney(finalPrice)}</span>
        `;
        if (compareAtPrice && compareAtPrice > finalPrice) {
          const discount = Math.round((compareAtPrice - finalPrice) / compareAtPrice * 100);
          priceHTML += `<span class="cart-drawer__item-badge">${discount}% OFF</span>`;
        }
      } else {
        // No line-level discount
        priceHTML = `<span class="cart-drawer__item-final-price">${this.formatMoney(finalPrice)}</span>`;
        if (compareAtPrice && compareAtPrice > finalPrice) {
          const discount = Math.round((compareAtPrice - finalPrice) / compareAtPrice * 100);
          priceHTML += `
            <s class="cart-drawer__item-old-price">${this.formatMoney(compareAtPrice)}</s>
            <span class="cart-drawer__item-badge">${discount}% OFF</span>
          `;
        }
      }
      
      itemsHTML += `
        <div class="cart-drawer__item" id="CartDrawerItem-${index + 1}" data-index="${index + 1}" data-product-id="${item.product_id}">
          <button type="button" class="cart-drawer__item-remove" aria-label="Remove ${item.title}" data-index="${index + 1}">
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M0.833008 0.833984L8.83301 8.83399M0.833008 8.83399L8.83301 0.833984" stroke="#868783" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
          </button>
          ${item.image ? `
            <div class="cart-drawer__item-image">
              <img src="${item.image}" alt="${item.title}" loading="lazy" width="150" height="150">
            </div>
          ` : ''}
          <div class="cart-drawer__item-details">
            <a href="${item.url}" class="cart-drawer__item-title">${item.product_title}</a>
            ${item.subtitle ? `
              <p class="cart-drawer__item-subtitle">${this.escapeHtml(item.subtitle)}</p>
            ` : ''}
            <div class="cart-drawer__item-price">
              ${priceHTML}
            </div>
            <div class="cart-drawer__item-quantity">
              <div class="cart-drawer-quantity">
                <button class="cart-drawer-quantity__button" name="minus" type="button" data-index="${index + 1}">
                  <span class="visually-hidden">Decrease quantity</span>
                  <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-minus" fill="none" viewBox="0 0 10 2">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 110 1H1A.5.5 0 01.5 1z" fill="currentColor"></path>
                  </svg>
                </button>
                <input class="cart-drawer-quantity__input" type="number" name="updates[]" value="${item.quantity}" min="0" aria-label="Quantity" data-index="${index + 1}">
                <button class="cart-drawer-quantity__button" name="plus" type="button" data-index="${index + 1}">
                  <span class="visually-hidden">Increase quantity</span>
                  <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-plus" fill="none" viewBox="0 0 10 10">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M1 4.51a.5.5 0 000 1h3.5l.01 3.5a.5.5 0 001-.01V5.5l3.5-.01a.5.5 0 00-.01-1H5.5L5.49.99a.5.5 0 00-1 .01v3.5l-3.5.01H1z" fill="currentColor"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div class="cart-drawer__item-total">
            ${this.formatMoney(item.final_line_price)}
          </div>
        </div>
      `;
    });

    itemsContainer.innerHTML = itemsHTML;
  }

  async updateTotals(cartData) {
    // Calculate subtotal - match Liquid logic
    // Liquid uses: line_item.product.compare_at_price || line_item.product.price
    // Since cart.js doesn't include product.compare_at_price, we need to fetch it
    let subtotal = 0;
    
    // Fetch product data to get compare_at_price for each item
    const pricePromises = cartData.items.map(async (item) => {
      try {
        // Try to get compare_at_price from product data
        // Fetch product JSON to get compare_at_price
        const productResponse = await fetch(`/products/${item.handle || item.product_id}.js`);
        if (productResponse.ok) {
          const productData = await productResponse.json();
          // Find the variant that matches this cart item
          const variant = productData.variants?.find(v => v.id === item.variant_id);
          if (variant && variant.compare_at_price) {
            return variant.compare_at_price * item.quantity;
          }
        }
      } catch (e) {
        // Ignore errors, fall back to original_price
      }
      // Fallback: use original_price (which is the regular price)
      return item.original_price * item.quantity;
    });
    
    const prices = await Promise.all(pricePromises);
    subtotal = prices.reduce((sum, price) => sum + price, 0);

    const discount = subtotal - cartData.total_price;
    const discountPercent = subtotal > 0 ? Math.round((discount / subtotal) * 100) : 0;

    // Update summary
    const totalPrice = this.querySelector('.cart-drawer__total-price');
    if (totalPrice) {
      totalPrice.textContent = this.formatMoney(cartData.total_price);
    }

    // Always check if savings element exists and update it
    const summary = this.querySelector('.cart-drawer__summary');
    if (!summary) {
      console.warn('cart-drawer__summary not found, cannot update savings');
      return; // Summary doesn't exist, can't update
    }
    
    let savings = summary.querySelector('.cart-drawer__savings');
    
    if (discount > 0) {
      if (!savings) {
        // Create savings element if it doesn't exist
        savings = document.createElement('div');
        savings.className = 'cart-drawer__savings';
        savings.innerHTML = `
          <span class="cart-drawer__savings-label">My Savings</span>
          <span class="cart-drawer__savings-amount">-${this.formatMoney(discount)} (${discountPercent}%)</span>
        `;
        // Insert after the total div, before any other elements
        const totalDiv = summary.querySelector('.cart-drawer__total');
        if (totalDiv) {
          // Insert right after total div
          if (totalDiv.nextSibling) {
            totalDiv.parentNode.insertBefore(savings, totalDiv.nextSibling);
          } else {
            totalDiv.parentNode.appendChild(savings);
          }
        } else {
          // If total div doesn't exist, append to summary
          summary.appendChild(savings);
        }
      } else {
        // Update existing savings element - ensure it has the correct structure
        let savingsAmount = savings.querySelector('.cart-drawer__savings-amount');
        if (!savingsAmount) {
          // If amount element doesn't exist, recreate the whole savings element
          savings.innerHTML = `
            <span class="cart-drawer__savings-label">My Savings</span>
            <span class="cart-drawer__savings-amount">-${this.formatMoney(discount)} (${discountPercent}%)</span>
          `;
        } else {
          // Update just the amount
          savingsAmount.textContent = `-${this.formatMoney(discount)} (${discountPercent}%)`;
        }
      }
    } else {
      // Remove savings if discount is 0 or negative
      if (savings) {
        savings.remove();
      }
    }
    
    // Store reference to ensure it persists across updates
    if (discount > 0 && savings) {
      this._savingsElement = savings;
    } else {
      this._savingsElement = null;
    }
  }

  async updateFooterSummary(cartData) {
    const footerSummary = this.querySelector('#cart-drawer-footer-summary');
    if (!footerSummary) return;

    // Calculate subtotal (same logic as updateTotals)
    let subtotal = 0;
    const pricePromises = cartData.items.map(async (item) => {
      try {
        const productUrl = item.url || `/products/${item.handle || item.product_id}`;
        const productHandle = productUrl.match(/\/products\/([^\/\?]+)/)?.[1];
        
        if (productHandle) {
          const productResponse = await fetch(`/products/${productHandle}.js`);
          if (productResponse.ok) {
            const productData = await productResponse.json();
            const variant = productData.variants?.find(v => v.id === item.variant_id);
            if (variant && variant.compare_at_price) {
              return variant.compare_at_price * item.quantity;
            }
          }
        }
      } catch (e) {
        // Ignore errors
      }
      return item.original_price * item.quantity;
    });
    
    const prices = await Promise.all(pricePromises);
    subtotal = prices.reduce((sum, price) => sum + price, 0);

    const discount = subtotal - cartData.total_price;
    const discountPercent = subtotal > 0 ? Math.round((discount / subtotal) * 100) : 0;

    // Update footer elements
    const footerSubtotal = footerSummary.querySelector('.cart-drawer__footer-subtotal');
    if (footerSubtotal) {
      footerSubtotal.textContent = this.formatMoney(subtotal);
    }

    const footerTotal = footerSummary.querySelector('.cart-drawer__footer-total');
    if (footerTotal) {
      footerTotal.textContent = this.formatMoney(cartData.total_price);
    }

    const savingsRow = footerSummary.querySelector('#cart-drawer-footer-savings-row');
    const footerSavings = footerSummary.querySelector('.cart-drawer__footer-savings');
    
    if (discount > 0) {
      if (savingsRow) {
        savingsRow.style.display = 'flex';
      }
      if (footerSavings) {
        footerSavings.textContent = `-${this.formatMoney(discount)} (${discountPercent}%)`;
      }
    } else {
      if (savingsRow) {
        savingsRow.style.display = 'none';
      }
    }
  }

  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  formatMoney(cents) {
    const amount = cents / 100;
    
    // Use Shopify's money format if available
    if (window.Shopify && window.Shopify.formatMoney) {
      // Get money format from theme settings or use default
      const moneyFormat = window.money_format || window.Shopify.money_format || '${{amount}}';
      
      // Format the amount without trailing zeros
      const formattedAmount = parseFloat(amount.toFixed(2)).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
      
      // Replace {{amount}} in the format string
      const formatted = moneyFormat.replace('{{amount}}', formattedAmount);
      return formatted;
    }
    
    // Fallback: format without currency code (just amount with $)
    // Remove trailing zeros
    try {
      const formatted = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(amount);
      return `$${formatted}`;
    } catch (e) {
      // Remove trailing zeros manually
      const amountStr = amount.toFixed(2);
      const cleaned = amountStr.replace(/\.?0+$/, '');
      return `$${cleaned}`;
    }
  }

  initializeEventListeners() {
    // Re-attach delivery option listeners
    this.deliveryOptions = this.querySelectorAll('.cart-drawer__delivery-option');
    this.deliveryOptions?.forEach(option => {
      option.addEventListener('click', this.handleDeliveryOption.bind(this));
    });

    // Restore selected delivery option
    if (this.selectedDeliveryType) {
      const selectedOption = this.querySelector(`[data-delivery-type="${this.selectedDeliveryType}"]`);
      if (selectedOption) {
        this.deliveryOptions.forEach(opt => opt.classList.remove('cart-drawer__delivery-option--active'));
        selectedOption.classList.add('cart-drawer__delivery-option--active');
      }
    }
  }

  async loadRecommendations() {
    const recommendationsContainer = this.querySelector('#cart-drawer-recommendations');
    if (!recommendationsContainer) return;

    try {
      // Get recommended products from settings
      const recommendedProducts = window.cartDrawerRecommendedProducts || [];
      
      if (!recommendedProducts || recommendedProducts.length === 0) {
        recommendationsContainer.innerHTML = '';
        return;
      }
      
      // Get current cart items to filter out products that are already in cart
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();
      // Convert both to strings for reliable comparison
      const cartProductIds = new Set(cartData.items.map(item => String(item.product_id)));
      
      // Filter out products that are already in cart
      const availableProducts = recommendedProducts.filter(product => {
        if (!product || !product.id) return false;
        // Convert product ID to string for comparison
        const productIdStr = String(product.id);
        return !cartProductIds.has(productIdStr);
      });
      
      if (availableProducts.length === 0) {
        recommendationsContainer.innerHTML = '';
        return;
      }
      
      // Build HTML for recommended products
      let html = '';
      availableProducts.forEach(product => {
        const image = product.featured_image || '';
        const title = product.title || '';
        const subtitle = product.subtitle || '';
        const price = this.formatMoney(product.price);
        const comparePrice = product.compare_at_price ? this.formatMoney(product.compare_at_price) : null;
        const url = product.url || `/products/${product.handle}`;
        
        html += `
          <div class="cart-drawer__recommendation-item" data-product-id="${product.id}">
            ${image ? `<div class="cart-drawer__recommendation-image"><a href="${url}"><img src="${image}" alt="${title}" loading="lazy" width="200" height="200"></a></div>` : ''}
            <div class="cart-drawer__recommendation-details">
              <a href="${url}" class="cart-drawer__recommendation-title">${title}</a>
              ${subtitle ? `<p class="cart-drawer__recommendation-subtitle">${this.escapeHtml(subtitle)}</p>` : ''}
              <div class="cart-drawer__recommendation-price">
                ${comparePrice ? `<s class="cart-drawer__recommendation-old-price">${comparePrice}</s>` : ''}
                <span class="cart-drawer__recommendation-final-price">${price}</span>
              </div>
              <button type="button" class="cart-drawer__recommendation-add" data-product-id="${product.id}" data-variant-id="${product.variant_id || ''}" data-product-url="${url}">+ ADD</button>
            </div>
          </div>
        `;
      });
      
      recommendationsContainer.innerHTML = html;
      this.attachRecommendationListeners();
    } catch (error) {
      console.error('Error loading recommendations:', error);
      const recommendationsContainer = this.querySelector('#cart-drawer-recommendations');
      if (recommendationsContainer) {
        recommendationsContainer.innerHTML = '';
      }
    }
  }

  formatRecommendations(recommendations) {
    const products = recommendations.querySelectorAll('.grid__item');
    if (products.length === 0) return '';

    let html = '';
    products.forEach(product => {
      const card = product.querySelector('.card-product2');
      if (!card) return;

      const image = card.querySelector('img');
      const title = card.querySelector('.card-product2__title');
      const subtitle = card.querySelector('.card-product2__subtitle');
      const price = card.querySelector('.card-product2__prices .price, .price');
      const rating = card.querySelector('.rating');
      const productLink = title?.href || card.querySelector('a')?.href || '#';
      const variantId = card.querySelector('[data-variant-id]')?.dataset.variantId || card.querySelector('form')?.dataset.productId || '';

      html += `
        <div class="cart-drawer__recommendation-item">
          ${image ? `<div class="cart-drawer__recommendation-image"><a href="${productLink}">${image.outerHTML}</a></div>` : ''}
          <div class="cart-drawer__recommendation-details">
            ${title ? `<a href="${productLink}" class="cart-drawer__recommendation-title">${title.textContent}</a>` : ''}
            ${rating ? `<div class="cart-drawer__recommendation-rating">${rating.outerHTML}</div>` : ''}
            ${subtitle ? `<p class="cart-drawer__recommendation-subtitle">${subtitle.textContent}</p>` : ''}
            ${price ? `<div class="cart-drawer__recommendation-price">${price.textContent.trim()}</div>` : ''}
            <button type="button" class="cart-drawer__recommendation-add" data-product-url="${productLink}">+ ADD</button>
          </div>
        </div>
      `;
    });

    return html;
  }

  attachRecommendationListeners() {
    const addButtons = this.querySelectorAll('.cart-drawer__recommendation-add');
    addButtons.forEach(button => {
      button.addEventListener('click', async (event) => {
        event.preventDefault();
        event.stopPropagation();
        
        const productId = button.dataset.productId;
        const variantId = button.dataset.variantId;
        const productUrl = button.dataset.productUrl;
        
        if (!productId || !variantId) {
          // If no variant ID, navigate to product page
          if (productUrl && productUrl !== '#') {
            window.location.href = productUrl;
          }
          return;
        }
        
        // Disable button during add
        button.disabled = true;
        button.textContent = 'Adding...';
        
        try {
          // Add product to cart
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: variantId,
              quantity: 1
            })
          });
          
          if (response.ok) {
            // Reload drawer to update cart
            await this.reloadDrawerFromServer();
            // Recommendations will be reloaded and the added product will be filtered out
          } else {
            const error = await response.json();
            console.error('Error adding product:', error);
            button.disabled = false;
            button.textContent = '+ ADD';
          }
        } catch (error) {
          console.error('Error adding product to cart:', error);
          button.disabled = false;
          button.textContent = '+ ADD';
        }
      });
    });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// Open cart drawer when cart icon is clicked
document.addEventListener('DOMContentLoaded', () => {
  // Don't open drawer on cart page
  if (window.location.pathname === '/cart' || window.location.pathname === '/cart/') {
    return;
  }

  const cartIcon = document.getElementById('cart-icon-bubble');
  const cartDrawer = document.getElementById('cart-drawer');
  
  if (cartIcon && cartDrawer) {
    cartIcon.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      cartDrawer.open();
    });
  }
  
  // Also handle data attribute
  document.querySelectorAll('[data-cart-drawer-toggle]').forEach(button => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (cartDrawer) {
        cartDrawer.open();
      }
    });
  });
});


